import {getIssuePhotoSrc, Issue, IssueStatus} from "../api/issue.ts";
import {FC, useState} from "react";
import Panel from "../atoms/Panel.tsx";
import {Column, Row} from "../atoms/Layout.tsx";
import {Text} from "../style/typography.tsx";
import {ArchiveTickIcon, ArrowRightIcon, CheckmarkIcon, LocationIcon} from "../atoms/Icon";
import PriorityCircle from "../atoms/PriorityCircle.tsx";
import {DateTime} from "luxon";
import {useNavigate} from "react-router";
import {useSite} from "../store/siteContext.ts";
import {css} from "@pandacss/css";
import PhotoReviewModal from "./PhotoReviewModal.tsx";
import HorizontalDivider from "../atoms/HorizontalDivider.tsx";

const photoCarousel = css({
    display: 'flex',
    gap: '12px',
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
});

const photoThumb = css({
    width: '80px',
    height: '80px',
    borderRadius: '8px',
    objectFit: 'cover',
    flexShrink: 0,
    cursor: 'pointer',
});

interface Props {
    issue: Issue
}

const formatDate = (ms: number): string => {
    const dt = DateTime.fromMillis(ms);
    const now = DateTime.local();

    return dt.hasSame(now, 'day')
        ? `Today ${dt.toFormat('HH:mm')}`
        : dt.toFormat('ccc dd LLL HH:mm');
}

const IssueListItem: FC<Props> = ({issue}) => {
    const site = useSite();
    const navigate = useNavigate();
    const [selectedPhoto, setSelectedPhoto] = useState<string | undefined>();

    const isResolved = issue.status === IssueStatus.RESOLVED;

    return <>
        {selectedPhoto && (
            <PhotoReviewModal src={selectedPhoto} onDismiss={() => setSelectedPhoto(undefined)}/>
        )}
        <Panel onClick={() => navigate(`/u/dashboard/site/${site.id}/issue/${issue.id}/details`)}>
            <Row gap="8">
                {isResolved ? <CheckmarkIcon size={20}/> :
                    <PriorityCircle priority={issue.priority}/>}
                <Column grow gap="8">
                    <Text role="body" size="2" weight="medium">{issue.title}</Text>
                    {isResolved
                        ? <Row gap="8" centerVertically>
                            <Text role="body" size="4" weight="regular">{formatDate(issue.createdAt)}</Text>
                            <Row gap="4" centerVertically>
                                <ArchiveTickIcon size={12}/>
                                <Text role="body" size="4"
                                      weight="regular">Completed {formatDate(issue.closedAt!)}</Text>
                            </Row>
                        </Row>
                        : <Text role="body" size="4" weight="regular">{formatDate(issue.createdAt)}</Text>
                    }

                    <Row gap="12">
                        {issue.location && (
                            <Row gap="4" centerVertically>
                                <LocationIcon size={12}/>
                                <Text role="body" size="4" weight="regular">{issue.location}</Text>
                            </Row>
                        )}
                        <Text role="body" size="4" weight="regular">{issue.category.name}</Text>
                    </Row>

                    {issue.photos.length > 0 && (
                        <>
                            <HorizontalDivider/>
                            <div className={photoCarousel}>
                                {issue.photos.map(photo => (
                                    <img
                                        key={photo.id}
                                        src={getIssuePhotoSrc(issue.id, photo.id)}
                                        className={photoThumb}
                                        style={{width: '80px', height: '80px'}}
                                        alt={photo.description}
                                        onClick={e => {
                                            e.stopPropagation();
                                            setSelectedPhoto(getIssuePhotoSrc(issue.id, photo.id));
                                        }}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </Column>
                <ArrowRightIcon size={16}/>
            </Row>
        </Panel>
    </>
}

export default IssueListItem;
